/**
 * Route-level Error Fallback Component
 *
 * Displays a user-friendly error message when a route fails to load.
 * Provides options to retry or navigate back.
 */

import { AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

export interface ErrorFallbackProps {
  error?: Error;
  resetError?: () => void;
  onGoBack?: () => void;
}

export function ErrorFallback({ error, resetError, onGoBack }: ErrorFallbackProps) {
  const { t } = useTranslation('common');

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertCircle className="h-6 w-6 text-destructive" />
            <CardTitle>{t('errorFallback.title')}</CardTitle>
          </div>
          <CardDescription>
            {t('errorFallback.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3">
              <p className="text-sm text-destructive font-mono">
                {error.message || t('errorFallback.unknownError')}
              </p>
            </div>
          )}
          <div className="flex gap-2">
            {resetError && (
              <Button onClick={resetError} className="flex-1">
                {t('errorFallback.tryAgain')}
              </Button>
            )}
            {onGoBack && (
              <Button onClick={onGoBack} variant="outline" className="flex-1">
                {t('errorFallback.goBack')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
